import { normalizeForMatching } from 'features/knowledgeDocumentViewer/contexts/TimeSeriesLinkContext'

import type { SubjectIndexRow } from './subjectsIndex'

export interface SubjectLinkTarget {
  uid: string
  name: string
}

export interface SubjectLinkMatch {
  start: number
  end: number
  uid: string
  name: string
}

/** Shorter normalized keys are too noisy to safely auto-link (single letters,
 * two/three-char codes shared across many subjects). */
export const MIN_SUBJECT_LINK_KEY_LENGTH = 4

/**
 * Normalized (uppercased, separator-stripped) terms that read as instrument
 * tags but are not — acronyms, date/time formats, and stock phrases seen
 * false-positiving in the chatbot's own identifier extractor
 * (`features/chatbot/utils/extractIdentifierCandidates.ts`).
 */
export const SUBJECT_LINK_STOPWORDS: ReadonlySet<string> = new Set([
  'AIGENERATED',
  'AIPOWERED',
  'AIBASED',
  'USEASTERN',
  'USCENTRAL',
  'USPACIFIC',
  'ISO8601',
  'ISO9001',
  'UTF8',
  'HTTPGET',
  'HTTPPOST',
  'JSONLD',
  'YYYYMM',
  'YYYYMMDD',
  'HHMM',
  'DDMM',
  'MMDD',
  'LGINFO',
  'PDPAGE',
  'RECHECK',
  'REOPEN',
  'PREEXISTING',
  'NONRETURN',
  'ONOFF',
  'RCAROOT',
  'HIGHTEMP',
  'LOWTEMP',
  'HIGHPRESSURE',
  'LOWPRESSURE',
  'LONGTERM',
  'SHORTTERM',
  'REALTIME',
  '24X7',
  'COVID19',
])

const isRejectedKey = (key: string): boolean =>
  key.length < MIN_SUBJECT_LINK_KEY_LENGTH ||
  /^\d+$/.test(key) ||
  SUBJECT_LINK_STOPWORDS.has(key)

/**
 * Lookup index from every normalized subject name/alias to the subject it
 * names — the dictionary `findSubjectLinkMatches` scans text against. A key
 * that resolves to more than one DIFFERENT subject is ambiguous and dropped
 * entirely rather than guessed at; the same key reached via two aliases of
 * the SAME subject is fine and kept.
 */
export const buildSubjectLinkIndex = (
  rows: readonly SubjectIndexRow[]
): Map<string, SubjectLinkTarget> => {
  const index = new Map<string, SubjectLinkTarget>()
  const ambiguous = new Set<string>()

  for (const row of rows) {
    const candidates = new Set([
      row.name,
      ...row.aliasValues,
      ...row.aliasNorms,
    ])
    for (const candidate of candidates) {
      const key = normalizeForMatching(candidate)
      if (isRejectedKey(key)) continue

      const existing = index.get(key)
      if (existing) {
        if (existing.uid !== row.uid) ambiguous.add(key)
        continue
      }
      index.set(key, { uid: row.uid, name: row.name })
    }
  }

  for (const key of ambiguous) index.delete(key)
  return index
}

interface Token {
  start: number
  end: number
  text: string
}

const TOKEN_PATTERN = /[A-Za-z0-9][A-Za-z0-9._/-]*/g
const TRAILING_SEPARATOR = /[.,\-_/]$/

const tokenize = (text: string): Token[] => {
  const tokens: Token[] = []
  const pattern = new RegExp(TOKEN_PATTERN)
  let match: RegExpExecArray | null
  while ((match = pattern.exec(text)) !== null) {
    let raw = match[0]
    let end = match.index + raw.length
    while (raw.length > 0 && TRAILING_SEPARATOR.test(raw)) {
      raw = raw.slice(0, -1)
      end -= 1
    }
    if (raw.length === 0) continue
    tokens.push({ start: match.index, end, text: raw })
  }
  return tokens
}

const MAX_WINDOW = 3
const HAS_DIGIT = /\d/

/**
 * Non-overlapping subject-name/alias matches in free text, longest-window
 * first (3, 2, 1 tokens) at every scan position. Every candidate must contain
 * an ASCII digit — a hard guard so ordinary prose (e.g. "real-time") can
 * never linkify even if some stopword slipped through the index.
 */
export function findSubjectLinkMatches(
  text: string,
  index: ReadonlyMap<string, SubjectLinkTarget>
): SubjectLinkMatch[] {
  const tokens = tokenize(text)
  const matches: SubjectLinkMatch[] = []
  let i = 0

  while (i < tokens.length) {
    let advanced = false
    for (let window = MAX_WINDOW; window >= 1; window -= 1) {
      if (i + window > tokens.length) continue
      const first = tokens[i]
      const last = tokens[i + window - 1]
      const raw = text.slice(first.start, last.end)
      if (!HAS_DIGIT.test(raw)) continue
      const key = normalizeForMatching(raw)
      if (key.length < MIN_SUBJECT_LINK_KEY_LENGTH) continue
      const target = index.get(key)
      if (!target) continue
      matches.push({
        start: first.start,
        end: last.end,
        uid: target.uid,
        name: target.name,
      })
      i += window
      advanced = true
      break
    }
    if (!advanced) i += 1
  }

  return matches
}
